import React, {Component} from 'react';

import {shouldPopout} from '../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {DraftMessageModel} from '../../../libs/shared/core/src/models/api/messageModel';
import {CellsContext} from '../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ComposerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {MultiButtonsDialog} from '../../../libs/shared/ui/src/components/dialogs/multiButtonsDialog';
import {HandoffActivityTypesEnum} from '../../../srcCommon/app/handoff/handoffModel';
import {ConversationModel} from '../../models/api/conversationModel';
import {UserActivity} from '../app/userActivity/userActivity';
import {Frame} from '../common/frames/frame';
import {FrameTitle} from '../common/frames/frameTitle';
import {ExpandedDarkThemeProvider} from '../common/theme/expandedDarkThemeProvider';
import {ComposerContainer} from '../inboxes/composer/composerContainer';
import {ComposerErrorBoundary} from '../inboxes/composer/composerErrorBoundary';
import {RequiredTaggingCoordinator} from '../inboxes/viewer/conversation/safetyNets/requiredTaggingCoordinator';
import {SafetyNetsProvider} from '../inboxes/viewer/conversation/safetyNets/safetyNetsContext';

/*
 * Props.
 */

interface PopupComposerProps {
  panelId: ComposerPanelId;
  cellsContext?: CellsContext;
  message?: DraftMessageModel;
  isMessageShared: boolean;
  conversation: ConversationModel | null;
  deleteMessage: () => void;
  onForcePopout: () => void;
  onRequestClose: () => void;
  shouldForcePopout: boolean | undefined;
  timelineVariation: TimelineVariationsEnum;
}

/*
 * Component.
 */

enum SaveOrDeleteModesEnum {
  NONE,
  POPOUT,
  MAIN_WINDOW,
}

interface PopupComposerState {
  saveOrDeleteMode: SaveOrDeleteModesEnum;
}

export class PopupComposer extends Component<PopupComposerProps, PopupComposerState> {
  constructor(props: PopupComposerProps) {
    super(props);
    this.state = {saveOrDeleteMode: SaveOrDeleteModesEnum.NONE};
  }

  /*
   * Lifecycle.
   */

  componentDidUpdate() {
    // If we have a message, nothing to do.
    const {message, onRequestClose} = this.props;
    if (message || !onRequestClose) {
      return;
    }

    // If it was deleted, close the popup.
    onRequestClose();
  }

  /*
   * Event handlers.
   */

  private readonly onSaveOrDeleteRequestClose = () => {
    this.setState(({saveOrDeleteMode}) => {
      const {onRequestClose} = this.props;
      if (saveOrDeleteMode === SaveOrDeleteModesEnum.MAIN_WINDOW) {
        // TODO: Avoid side effects in state reducers.
        onRequestClose();
        return null;
      }

      return {saveOrDeleteMode: SaveOrDeleteModesEnum.NONE};
    });
  };

  private readonly onRequestClose = (forceClose?: boolean) => {
    this.setState(({saveOrDeleteMode}) => {
      const {message, onRequestClose, isMessageShared} = this.props;

      // If the message was saved and not shared, ask for confirmation.
      const isSaveOrDeleteOpen = saveOrDeleteMode !== SaveOrDeleteModesEnum.NONE;
      const isSaved = message && message.updatedAt;
      const isShared = message && isMessageShared;
      const isInConversation = message && message.inReplyToId;
      if (!isSaveOrDeleteOpen && isSaved && !isInConversation && !isShared) {
        const newSaveOrDeleteMode = forceClose
          ? SaveOrDeleteModesEnum.MAIN_WINDOW
          : SaveOrDeleteModesEnum.POPOUT;
        return {saveOrDeleteMode: newSaveOrDeleteMode};
      }

      // Otherwise, close the popup.
      // TODO: Avoid side effects in state reducers.
      onRequestClose();

      return null;
    });
  };

  /*
   * Render.
   */

  render() {
    // If the message could not be found, no need to display anything.
    const {message, conversation, cellsContext, panelId, shouldForcePopout, timelineVariation} = this.props;
    if (!message) {
      return null;
    }

    const {saveOrDeleteMode} = this.state;
    const displaySaveOrDeleteInPopout = saveOrDeleteMode === SaveOrDeleteModesEnum.POPOUT;
    const displaySaveOrDeleteInMainWindow = saveOrDeleteMode === SaveOrDeleteModesEnum.MAIN_WINDOW;

    // If we're rendering in the main window, no need for the frame.
    if (displaySaveOrDeleteInMainWindow) {
      return this.renderSaveOrDeleteDialog();
    }

    const isDialogPopout = !shouldPopout(shouldForcePopout);
    const onForcePopout = isDialogPopout ? this.props.onForcePopout : undefined;
    const onRequestClose = isDialogPopout ? () => this.onRequestClose(false) : undefined;

    // Otherwise, render the composer.
    const composerJsx = (
      <SafetyNetsProvider>
        <Frame
          onRequestClose={this.onRequestClose}
          shouldForcePopout={shouldForcePopout}
          minWidth="540px"
          width={740}
          startHeight={560}
          interactionId="popup-composer"
          shouldHideDialogHeader
        >
          <UserActivity type={HandoffActivityTypesEnum.MESSAGE_COMPOSE} userInfo={{draftUid: message.uid}} />
          <FrameTitle>
            <Trans id="composer-popup-title">New Message</Trans>
          </FrameTitle>
          <RequiredTaggingCoordinator />
          <ComposerErrorBoundary message={message} timelineVariation={timelineVariation}>
            <ComposerContainer
              panelId={panelId}
              message={message}
              conversation={conversation}
              cellsContext={cellsContext}
              canPopout={false}
              onForcePopout={onForcePopout}
              onRequestClose={onRequestClose}
              isOnlyComposer
            />
          </ComposerErrorBoundary>
          {displaySaveOrDeleteInPopout && this.renderSaveOrDeleteDialog()}
        </Frame>
      </SafetyNetsProvider>
    );

    return <ExpandedDarkThemeProvider>{composerJsx}</ExpandedDarkThemeProvider>;
  }

  private renderSaveOrDeleteDialog() {
    const {deleteMessage, onRequestClose} = this.props;
    return (
      <MultiButtonsDialog
        title={<Trans id="composer-saveordelete-title">Save this draft?</Trans>}
        primaryButtonLabel={<Trans id="composer-saveordelete-save">Save</Trans>}
        secondaryButtonLabel={<Trans id="composer-saveordelete-dontsave">Don&apos;t save</Trans>}
        onPrimaryClick={onRequestClose}
        onSecondaryClick={deleteMessage}
        onRequestClose={this.onSaveOrDeleteRequestClose}
      >
        <Trans id="composer-saveordelete">Would you like to save this draft to work on it later?</Trans>
      </MultiButtonsDialog>
    );
  }
}
